import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import LabeledInput from "../LabeledInput";
import { getFullAccountByAccountId } from "../../api/api";
import { AiOutlineLoading } from "react-icons/ai";
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import useTheme from "../../hooks/useTheme";
import { useNullDate } from '../../hooks/useNullDate';
import { formatPercentage } from '../../lib/utils';
import { useTranslateProduct } from '../../hooks/useTranslateProduct';
import { useTranslateDetails } from '../../hooks/useTranslateDetails';
import { useCurrencyFormatter } from "../../hooks/useCurrencyFormatter";
import { useTranslation } from 'react-i18next';

dayjs.locale('en');

"dealView.details.month"

function Details({ accountID, activeTab }) {
  const [accountDetails, setAccountDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const translateField = useTranslateDetails();
  const formatCurrency = useCurrencyFormatter();
  const { t } = useTranslation();

  useTheme();
  const translateProduct = useTranslateProduct();
  const formatDate = useNullDate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const apiData = await getFullAccountByAccountId(accountID);
        if (apiData) {
          setAccountDetails(apiData);
        } else {
          console.error('Failed to fetch data for Deal Details.');
        }
      } catch (error) {
        console.error('Error fetching data for Deal Details:', error.message);
      } finally {
        setLoading(false);
      }
    };

    if (activeTab === "Details" || activeTab === "Détails") {
      fetchData();
    }
  }, [accountID, activeTab]);

  const detailsToDisplay = [
    { label: "Status", value: translateField('loanStatus', accountDetails.status) },
    { label: "IsCommitmentIssued", value: translateField('isCommitmentIssued', accountDetails.isCommitmentIssued ? "Yes" : "No") },
    { label: "LoanPurpose", value: translateField('loanPurpose', accountDetails.loanPurpose) },
    { label: "ApplicationDate", value: formatDate(accountDetails.applicationDate) },
    { label: "Position", value: translateField('position', accountDetails.position) },
    { label: "SolicitorInstructionDate", value: formatDate(accountDetails.solicitorInstructionDate) },
    { label: "Product", value: translateProduct(accountDetails.product) },
    { label: "RateType", value: translateField('rateType', accountDetails.rateType) },
    { label: "PurchasePrice", value: formatCurrency(accountDetails.purchasePrice) },
    { label: "TotalPurchasePrice", value: formatCurrency(accountDetails.totalPurchasePrice) },
    { label: "TotalDownPayment", value: formatCurrency(accountDetails.totalDownPayment) },
    { label: "QualGDS", value: `${accountDetails.qualGDS}%` },
    { label: "InterestAdjustmentDate", value: formatDate(accountDetails.interestAdjustmentDate) },
    { label: "InterestRate", value: `${accountDetails.interestRate}%` },
    { label: "PaymentFrequency", value: translateField('paymentFrequency', accountDetails.paymentFrequency) },
    { label: "Amortization", value: `${accountDetails.amortization} ${t("dealView.details.month")}` },
    { label: "AnnualTaxAmount", value: formatCurrency(accountDetails.annualTaxAmount) },
    { label: "TaxHoldBack", value: formatCurrency(accountDetails.taxHoldBack) },
    { label: "InsurancePremium", value: formatCurrency(accountDetails.insurancePremium) },
    { label: "SubmissionAgent", value: accountDetails.submissionAgent },
    { label: "Underwriter", value: accountDetails.underwriter },
    { label: "SubStatus", value: translateField('loanSubStatus', accountDetails.subStatus) },
    { label: "PortalStatus", value: translateField('portalStatus', accountDetails.portalStatus) },
    { label: "LoanSubPurpose", value: translateField('loanSubPurpose', accountDetails.loanSubPurpose) },
    { label: "ClosingDate", value: formatDate(accountDetails.closingDate) },
    { label: "RateExpiry", value: formatDate(accountDetails.rateExpiry) },
    { label: "OutstandingConditions", value: accountDetails.outstandingConditions },
    { label: "LOB", value: translateField('lob', accountDetails.lob) },
    { label: "BuyDown", value: formatCurrency(accountDetails.buyDown) },
    { label: "AppraisalValue", value: formatCurrency(accountDetails.apprasialValue) },
    { label: "LTV", value: formatPercentage(accountDetails.ltv) },
    { label: "TotalMortgageAmount", value: formatCurrency(accountDetails.totalMortgageAmount) },
    { label: "QualTDS", value: `${accountDetails.qualTDS}%` },
    { label: "InterestAdjustmentAmount", value: formatCurrency(accountDetails.interestAdjustmentAmount) },
    { label: "FirstPaymentDate", value: formatDate(accountDetails.firstPaymentDate) },
    { label: "TotalPaymentAmount", value: formatCurrency(accountDetails.totalPaymentAmount) },
    { label: "PropertyTaxPortion", value: formatCurrency(accountDetails.propertyTaxPortion) },
    { label: "PropertyTaxPayer", value: translateField('propertyTaxPayer', accountDetails.propertyTaxPayer) },
    { label: "MortgageInsuranceProvider", value: accountDetails.mortgageInsuranceProvider },
    { label: "InsurancePremiumPayor", value: translateField('insurancePremiumPayor', accountDetails.insurancePremiumPayor) },
    { label: "BrokerAgent", value: accountDetails.brokerAgent },
    { label: "UnderwriterEmail", value: accountDetails.underwriterEmail },
  ];

  const midpoint = Math.ceil(detailsToDisplay.length / 2);

  return (
    <>
      {!loading ? (
        <>
          <div className="w-full flex flex-col gap-5">
            {detailsToDisplay.slice(0, midpoint).map(({ label, value }) => (
              <LabeledInput key={label} label={label} value={value} type={'string'} />
            ))}
          </div>
          <div className="w-full flex flex-col gap-5">
            {detailsToDisplay.slice(midpoint).map(({ label, value }) => (
              <LabeledInput key={label} label={label} value={value} type={'string'} />
            ))}
          </div>
        </>
      ) : (
        <div className='w-full h-full flex justify-center p-10'>
          <AiOutlineLoading className='animate-spin size-10'/>
        </div>
      )}
    </>
  );
}

Details.propTypes = {
  accountID: PropTypes.string.isRequired,
  activeTab: PropTypes.string.isRequired,
};

export default Details;